import i18n from 'i18next'
import { isCoverageEventId } from '../CycleCounts/events/coverageEventLinks'

const T = 'found_stock.errors'

// The curated set of error_codes this feature gives dedicated copy to: the ones the settlement and
// correction endpoints raise in practice (platform docs/features/2026-09-08-found-stock-settlement.md),
// plus 'NETWORK' — this file's own code for a response-less failure, so its dedicated key is
// reachable. Deliberately NOT the controller's whole vocabulary: anything else (WAREHOUSE_NOT_FOUND,
// a LOT_* code) falls through to the UNKNOWN template, which names the code it could not translate.
export const KNOWN_CODES = new Set([
  'NETWORK',
  'FOUND_STOCK_DISABLED',
  'UNITS_ALREADY_SETTLED',
  'UNITS_NOT_FOUND_STOCK',
  'UNITS_OUTSIDE_WAREHOUSE',
  'CONCURRENT_MODIFICATION',
  'INSUFFICIENT_LOST_CANDIDATES',
  'TOO_MANY_DECLARATIONS',
  'TOO_MANY_UNITS',
  'QUANTITY_INVALID',
  'INVALID_PARAMETER',
  'MULTIPLE_STORES',
  'STORE_NOT_FOUND',
  'BILLING_CONFIG_MISSING',
  'REPLENISHMENT_INVOICE_REQUIRED',
  'DECLARATION_SETTLED',
  'DECLARATION_INCONSISTENT',
  'DECLARATION_NOT_FOUND',
  'DECLARATION_IN_FROZEN_PROPOSAL',
  'PRODUCT_NOT_FOUND',
  'LOCATION_NOT_ELIGIBLE_FOR_FOUND_STOCK',
  'IDEMPOTENCY_CONFLICT',
  'UNIT_CODE_PRODUCTS_NOT_SUPPORTED',
  'INTERNAL_ERROR',
])

// Codes that mean the page's view of the queue is stale: the caller refetches after showing them.
const REFETCH_CODES = new Set([
  'UNITS_ALREADY_SETTLED',
  'UNITS_NOT_FOUND_STOCK',
  'UNITS_OUTSIDE_WAREHOUSE',
  'INSUFFICIENT_LOST_CANDIDATES',
  'DECLARATION_SETTLED',
  'DECLARATION_NOT_FOUND',
  'PRODUCT_NOT_FOUND',
])

const joinIds = ids => (Array.isArray(ids) ? ids.join(', ') : '')

// react-query `retry` option for every found-stock read. Only a failure that could plausibly succeed
// on a second attempt is retried — no response at all, or a 5xx. Every 4xx is final: a 404 product
// or a 422 stays a 404 or a 422, and a Pundit 401 would cost another refresh_token round-trip
// through the axios interceptor on each try.
export const retryUnlessUnauthorized = (failureCount, error) => {
  const status = error?.response?.status
  if (status != null && status < 500) return false
  return failureCount < 2
}

export const localizeFoundStockError = error => {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  if (status === 401) {
    return { code: 'UNAUTHORIZED', message: i18n.t(`${T}.UNAUTHORIZED`), gateOff: false, refetch: false, coverageEventId: null }
  }
  const code = data.error_code || (status ? 'INTERNAL_ERROR' : 'NETWORK')
  const context = data.context || {}
  // The frozen-proposal refusal names the event that owns the declaration; the caller links it.
  // Scoped to that one code: an event id riding on any other refusal links nothing. Only a real
  // event id is named or linked; anything else reads as a dash in the sentence.
  const coverageEventId = code === 'DECLARATION_IN_FROZEN_PROPOSAL' && isCoverageEventId(context.coverage_event_id) ? Number(context.coverage_event_id) : null
  if (code === 'FOUND_STOCK_DISABLED') {
    return { code, message: i18n.t(`${T}.FOUND_STOCK_DISABLED`), gateOff: true, refetch: false, coverageEventId: null }
  }
  const message = KNOWN_CODES.has(code)
    ? i18n.t(`${T}.${code}`, {
        requested: context.requested,
        available: context.available,
        product_ids: joinIds(context.product_ids),
        id: coverageEventId ?? '—',
        code,
      })
    : i18n.t(`${T}.UNKNOWN`, { code })
  return { code, message, gateOff: false, refetch: REFETCH_CODES.has(code), coverageEventId }
}
